// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ExternalLink from "@insite/mobius/Icons/ExternalLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import MapMarker from "@store/Library/Components/Icons/MapMarker";
import { AddressModel } from "@store/Library/Pages/EventDetailsPage";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React from "react";
import { css } from "styled-components";

interface Props {
    isLocalBranch?: boolean;
    localBranchTitle?: string;
    addressWrapper?: AddressModel[];
    showTitle?: boolean;
    showLink?: boolean;
    lightText?: boolean;
    extendedStyles?: EventAddressStyles;
}

export interface EventAddressStyles {
    addressWrapper?: InjectableCss;
    addressIcon?: IconPresentationProps;
    lightTextWrapper?: InjectableCss;
    titleAddress?: TypographyPresentationProps;
    textAddress?: TypographyPresentationProps;
    addressLink?: LinkPresentationProps;
}

export const eventAddressStyles: EventAddressStyles = {
    addressWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-wrap: nowrap;
            margin-top: ${pxToRem(15)};
        `,
    },
    addressIcon: {
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    lightTextWrapper: {
        css: css`
            color: white;
        `,
    },
    titleAddress: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
    textAddress: {
        css: css`
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    addressLink: {
        icon: {
            position: "right",
            iconProps: {
                src: ExternalLink,
                size: 16,
                color: "primary",
            },
        },
        css: css`
            ${linkStyles("primary.main", "primary.dark", true, false)}
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
};

const EventAddress = ({
    isLocalBranch,
    localBranchTitle,
    addressWrapper,
    showTitle,
    showLink,
    lightText,
    extendedStyles,
}: Props) => {
    const styles = useMergeProps(eventAddressStyles, extendedStyles);
    const locationFinderUrl = usePageLinkByPageType(constants.pageType.locationFinder)?.url;

    if ((!isLocalBranch && !addressWrapper?.length) || (isLocalBranch && !localBranchTitle)) {
        return null;
    }

    const linkAddress = (address: string) => `http://maps.google.com/maps?daddr=${address}`;

    const renderAddressText = (showLink: boolean | undefined, addressLabel: string, link: string | undefined) =>
        showLink && link ? (
            <Link href={link} target="_blank" {...styles.addressLink}>
                {addressLabel}
            </Link>
        ) : (
            addressLabel
        );

    return (
        <StyledWrapper {...styles.addressWrapper}>
            <Icon src={MapMarker} {...styles.addressIcon} color={lightText ? "white" : "primary"} />
            <StyledWrapper {...(lightText && styles.lightTextWrapper)}>
                {showTitle && <Typography {...styles.titleAddress}>{translate("Location")}</Typography>}
                <Typography {...(showTitle ? styles.textAddress : styles.titleAddress)}>
                    {isLocalBranch &&
                        localBranchTitle &&
                        renderAddressText(showLink, localBranchTitle, locationFinderUrl)}
                    {!isLocalBranch &&
                        addressWrapper?.map((address: AddressModel, i) => {
                            const { branchName, addressLabel } = address.fields;
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper key={`address-${i}`}>
                                    {branchName && <>{branchName} - </>}
                                    {renderAddressText(showLink, addressLabel, linkAddress(addressLabel))}
                                </StyledWrapper>
                            );
                        })}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default EventAddress;
